import React, { useMemo } from "react";
import { OverlayView } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";

interface DistanceLabelProps {
  position: google.maps.LatLngLiteral;
  distance: number; // in kilometers
  isReturnSegment?: boolean;
  segmentIndex?: number;
  backgroundColor?: string;
  onClick?: () => void;
  /** Pixel offset from position - useful to prevent overlapping labels */
  pixelOffset?: { x: number; y: number };
}

/**
 * DistanceLabel Component
 * Shows distance label on the map between two points
 */
const DistanceLabel: React.FC<DistanceLabelProps> = ({
  position,
  distance,
  isReturnSegment = false,
  segmentIndex,
  backgroundColor,
  onClick,
  pixelOffset,
}) => {
  const { t } = useTranslation();

  const formattedDistance = useMemo(() => {
    if (distance < 1) {
      return `${Math.round(distance * 1000)} ${t("m")}`;
    }
    return `${distance.toFixed(3)} ${t("km")}`;
  }, [distance, t]);

  // Default colors if not provided
  const defaultOutboundColor = "#3458a4";
  const defaultReturnColor = "#13a8a8";
  const bgColor = backgroundColor ?? (isReturnSegment ? defaultReturnColor : defaultOutboundColor);

  const containerStyle: React.CSSProperties = useMemo(() => ({
    position: "relative" as const,
    transform: "translate(-50%, -50%)",
  }), []);

  const labelStyle: React.CSSProperties = useMemo(() => ({
    backgroundColor: bgColor,
    color: "#fff",
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "13px",
    fontWeight: 700,
    whiteSpace: "nowrap",
    boxShadow: "0 3px 10px rgba(0,0,0,0.5)",
    cursor: onClick ? "pointer" : "default",
    border: "2px solid rgba(255,255,255,0.9)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
    lineHeight: 1,
    letterSpacing: "0.5px",
  }), [bgColor, onClick]);

  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.FLOAT_PANE}
      getPixelPositionOffset={() => ({ x: pixelOffset?.x ?? 0, y: pixelOffset?.y ?? 0 })}
    >
      <div style={containerStyle}>
        <div style={labelStyle} onClick={onClick}>
          {isReturnSegment && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
              <path d="M19 7v4H5.83l3.58-3.59L8 6l-6 6 6 6 1.41-1.41L5.83 13H21V7z"/>
            </svg>
          )}
          <span style={{ display: "inline-block" }}>{formattedDistance}</span>
        </div>
      </div>
    </OverlayView>
  );
};

export default React.memo(DistanceLabel);

/**
 * Calculate midpoint between two coordinates
 */
export const calculateMidpoint = (
  point1: google.maps.LatLngLiteral,
  point2: google.maps.LatLngLiteral
): google.maps.LatLngLiteral => {
  return {
    lat: (point1.lat + point2.lat) / 2,
    lng: (point1.lng + point2.lng) / 2,
  };
};

/**
 * Calculate distance between two points using Haversine formula
 */
export const calculateDistance = (
  point1: google.maps.LatLngLiteral,
  point2: google.maps.LatLngLiteral
): number => {
  const EARTH_RADIUS_KM = 6371;
  const toRadians = (degrees: number) => degrees * (Math.PI / 180);

  const dLat = toRadians(point2.lat - point1.lat);
  const dLng = toRadians(point2.lng - point1.lng);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(point1.lat)) *
      Math.cos(toRadians(point2.lat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_KM * c;
};
